import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Statistic, Button, Space, Typography, Spin, List } from 'antd';
import {
    ToolOutlined,
    ProjectOutlined,
    FileTextOutlined,
    TeamOutlined,
    PlusOutlined,
    RiseOutlined,
    ArrowUpOutlined,
} from '@ant-design/icons';
import { servicesAPI, portfolioAPI, newsAPI, teamAPI } from '../services/api';

const { Title } = Typography;

export default function Dashboard() {
    const [stats, setStats] = useState({
        services: 0,
        portfolio: 0,
        news: 0,
        team: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [services, portfolio, news, team] = await Promise.all([
                    servicesAPI.getAll(),
                    portfolioAPI.getAll(),
                    newsAPI.getAll(),
                    teamAPI.getAll(),
                ]);

                setStats({
                    services: services.data.length,
                    portfolio: portfolio.data.length,
                    news: news.data.length,
                    team: team.data.length,
                });
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading dashboard..." />
            </div>
        );
    }

    const quickActions = [
        { title: 'Add New Service', link: '/services/new', icon: <ToolOutlined />, type: 'primary' },
        { title: 'Add New Portfolio', link: '/portfolio/new', icon: <ProjectOutlined />, type: 'default' },
        { title: 'Add New Article', link: '/news/new', icon: <FileTextOutlined />, type: 'default' },
        { title: 'Add Team Member', link: '/team/new', icon: <TeamOutlined />, type: 'default' },
    ];

    return (
        <div>
            <Title level={2} style={{ marginBottom: 24 }}>Dashboard Overview</Title>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Services</span>}
                            value={stats.services}
                            valueStyle={{ color: '#fff' }}
                            prefix={<ToolOutlined />}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Portfolio Projects</span>}
                            value={stats.portfolio}
                            valueStyle={{ color: '#fff' }}
                            prefix={<ProjectOutlined />}
                            suffix={<ArrowUpOutlined style={{ fontSize: 16 }} />}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Articles</span>}
                            value={stats.news}
                            valueStyle={{ color: '#fff' }}
                            prefix={<FileTextOutlined />}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Team Members</span>}
                            value={stats.team}
                            valueStyle={{ color: '#fff' }}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card 
                        title={<span><RiseOutlined /> Quick Actions</span>}
                        bordered={false}
                        headStyle={{ background: '#fafafa' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {quickActions.map((action, index) => (
                                <Link key={index} to={action.link} style={{ display: 'block' }}>
                                    <Button
                                        type={action.type}
                                        icon={<PlusOutlined />}
                                        size="large"
                                        block
                                        style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
                                    >
                                        {action.icon} {action.title}
                                    </Button>
                                </Link>
                            ))}
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card 
                        title="📊 System Information"
                        bordered={false}
                        headStyle={{ background: '#fafafa' }}
                    >
                        <List
                            dataSource={[
                                { label: 'Total Content Items', value: stats.services + stats.portfolio + stats.news + stats.team },
                                { label: 'Active Services', value: stats.services },
                                { label: 'Published Projects', value: stats.portfolio },
                                { label: 'System Status', value: 'All systems operational ✅' },
                            ]}
                            renderItem={(item) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={item.label}
                                        description={<strong>{item.value}</strong>}
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
